import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { getVRPilotGuideRouteUrl } from './VRPilotGuide.routing';
import type { VRPilotGuideStep } from './VRPilotGuide.types';

const VRPilotGuideContext = createContext<
  | {
      activeStepId: string | undefined;
      clearActiveStepId: () => void;
      navigateToStep: (step: VRPilotGuideStep) => void;
      // Tips of read-only guides offer "Next step": the next open step, or
      // null on the last one. Undefined when the active step has no such tip.
      nextStep: VRPilotGuideStep | null | undefined;
      setActiveStepId: (stepId: string) => void;
      setNextStep: (step: VRPilotGuideStep | null | undefined) => void;
    }
  | undefined
>(undefined);

export const useVRPilotGuideContext = () => {
  const context = useContext(VRPilotGuideContext);

  if (!context) {
    throw new Error(
      'useVRPilotGuideContext must be used inside VRPilotGuideProvider.'
    );
  }

  return context;
};

export const useOptionalVRPilotGuideContext = () =>
  useContext(VRPilotGuideContext);

export const VRPilotGuideProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [activeStepId, setActiveStepId] = useState<string | undefined>();
  const [nextStep, setNextStep] = useState<VRPilotGuideStep | null>();
  const clearActiveStepId = useCallback(() => setActiveStepId(undefined), []);
  const navigateToStep = useCallback(
    (step: VRPilotGuideStep) => {
      setActiveStepId(step.id);
      navigate(getVRPilotGuideRouteUrl(step));
    },
    [navigate]
  );
  const value = useMemo(
    () => ({
      activeStepId,
      clearActiveStepId,
      navigateToStep,
      nextStep,
      setActiveStepId,
      setNextStep,
    }),
    [activeStepId, clearActiveStepId, navigateToStep, nextStep]
  );

  return (
    <VRPilotGuideContext.Provider value={value}>
      {children}
    </VRPilotGuideContext.Provider>
  );
};
